/**
 * LCFPIGeoMap.js — Lagos Climate Finance Intelligence Map
 * Shows project locations across Lagos LGAs colored by readiness tier.
 * Uses SafeLeafletMap wrapper for centralized StrictMode lifecycle safety.
 */
import React from "react";
import { TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SafeLeafletMap from "@/components/SafeLeafletMap";

// ── Lagos LGA coordinate registry ────────────────────────────────────────────
export const LAGOS_LGA_COORDS = {
  "Ikeja":          { lat: 6.5958, lng: 3.3417 },
  "Lagos Island":   { lat: 6.4541, lng: 3.4244 },
  "Victoria Island":{ lat: 6.4281, lng: 3.4219 },
  "Yaba":           { lat: 6.5093, lng: 3.3692 },
  "Apapa":          { lat: 6.4474, lng: 3.3692 },
  "Surulere":       { lat: 6.4974, lng: 3.3546 },
  "Alimosho":       { lat: 6.5900, lng: 3.2800 },
  "Ikorodu":        { lat: 6.6197, lng: 3.5102 },
  "Badagry":        { lat: 6.4200, lng: 2.8800 },
  "Mushin":         { lat: 6.5302, lng: 3.3522 },
  "Oshodi-Isolo":   { lat: 6.5377, lng: 3.3467 },
  "Kosofe":         { lat: 6.5790, lng: 3.4145 },
  "Shomolu":        { lat: 6.5403, lng: 3.3869 },
  "Agege":          { lat: 6.6239, lng: 3.3206 },
  "Lagos Mainland": { lat: 6.5144, lng: 3.3706 },
  "Epe":            { lat: 6.5872, lng: 3.9769 },
  "Eti-Osa":        { lat: 6.4686, lng: 3.5538 },
  "Ajeromi-Ifelodun":{ lat: 6.4618, lng: 3.3536 },
};

const TIER_COLORS = {
  "Tier 1": "#22c55e",
  "Tier 2": "#f59e0b",
  "Tier 3": "#ef4444",
  "default":"#94a3b8",
};

const ECO_COLORS = {
  Platinum:"#6366f1", Gold:"#d97706", Silver:"#64748b",
  Bronze:"#b45309", Participating:"#94a3b8",
};

/** Jitter projects at same LGA slightly so they don't overlap */
function jitterCoords(lat, lng, index, total) {
  if (total <= 1) return { lat, lng };
  const angle = (index / total) * 2 * Math.PI;
  const radius = 0.008 * Math.min(index, 3);
  return { lat: lat + radius * Math.cos(angle), lng: lng + radius * Math.sin(angle) };
}

const fmtU = v => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n}`;
};

export default function LCFPIGeoMap({
  projects = [],
  height = 480,
  showLegend = true,
  showHeatmap = true,
  heatmapMetric = "financing_gap",
  onProjectClick = null,
}) {
  const center = [6.5244, 3.3792]; // Lagos centroid
  const zoom   = 11;

  // Group projects by LGA
  const lgaGroups = {};
  projects.forEach(p => {
    const lga = p.lga || "Lagos Island";
    if (!lgaGroups[lga]) lgaGroups[lga] = [];
    lgaGroups[lga].push(p);
  });

  // Choropleth: compute metric per LGA for heatmap zones
  const lgaMetrics = {};
  Object.entries(lgaGroups).forEach(([lga, ps]) => {
    const totalFunding = ps.reduce((s, p) => s + (p.funding_need_usd || 0), 0);
    const t1 = ps.filter(p => p.readiness_tier === "Tier 1").length;
    const gap = totalFunding * (1 - t1 / Math.max(ps.length, 1));
    const avgReady = ps.reduce((s, p) => s + (p.readiness_score || 0), 0) / Math.max(ps.length, 1);
    lgaMetrics[lga] = { count: ps.length, t1, gap, avgReady, totalFunding };
  });

  // Max value for normalization
  const maxGap = Math.max(...Object.values(lgaMetrics).map(m => m.gap), 1);
  const maxCount = Math.max(...Object.values(lgaMetrics).map(m => m.count), 1);

  // Build markers with jitter — prefer actual geo coordinates, fall back to LGA centroid
  const markers = [];
  Object.entries(lgaGroups).forEach(([lga, ps]) => {
    const base = LAGOS_LGA_COORDS[lga] || LAGOS_LGA_COORDS["Lagos Island"];
    ps.forEach((p, i) => {
      let lat, lng;
      if (p.geo_lat && p.geo_lng && Math.abs(p.geo_lat) > 1 && Math.abs(p.geo_lng) > 1) {
        // Use actual stored coordinates
        lat = parseFloat(p.geo_lat);
        lng = parseFloat(p.geo_lng);
      } else {
        // Fall back to jittered LGA centroid
        const jittered = jitterCoords(base.lat, base.lng, i, ps.length);
        lat = jittered.lat;
        lng = jittered.lng;
      }
      const color = TIER_COLORS[p.readiness_tier] || TIER_COLORS.default;
      const ecoColor = ECO_COLORS[p.eco_label] || TIER_COLORS.default;
      const radius = Math.max(8, Math.min(18, (p.funding_need_usd || 0) / 500_000));
      markers.push({ ...p, lat, lng, color, ecoColor, radius });
    });
  });

  return (
    <div style={{ position:"relative", height, width:"100%" }}>
    <SafeLeafletMap
      center={center}
      zoom={zoom}
      style={{ height: "100%", width: "100%", background: "#0f172a" }}
      scrollWheelZoom={true}
      wrapperStyle={{ borderRadius:12, overflow:"hidden", border:"1px solid #e2e8f0", height:"100%", width:"100%" }}
      data-testid="lcfpi-geo-map"
    >
        {/* CartoDB Dark Matter tiles — institutional feel */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          maxZoom={18}
        />

        {/* Choropleth heatmap zones — LGA-level financing gap / density overlay */}
        {showHeatmap && Object.entries(LAGOS_LGA_COORDS).map(([lga, coords]) => {
          const m = lgaMetrics[lga];
          if (!m) return null;
          const intensity = heatmapMetric === "financing_gap" ? m.gap / maxGap :
                           heatmapMetric === "density" ? m.count / maxCount :
                           (1 - m.avgReady / 100);
          const heatColor = intensity > 0.6 ? "#ef4444" : intensity > 0.3 ? "#f59e0b" : "#22c55e";
          return (
            <CircleMarker key={`heat-${lga}`} center={[coords.lat, coords.lng]}
              radius={Math.max(20, Math.min(45, m.count * 8 + 16))}
              fillColor={heatColor} color="transparent" weight={0}
              fillOpacity={Math.min(0.25, 0.05 + intensity * 0.20)}>
              <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                <div style={{ fontFamily: "Arial", fontSize: 10 }}>
                  <strong>{lga}</strong><br/>
                  Projects: {m.count} (T1: {m.t1})<br/>
                  Gap: ${(m.gap/1e6).toFixed(1)}M<br/>
                  Avg Readiness: {m.avgReady.toFixed(0)}/100
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}

        {/* LGA labels */}
        {Object.entries(LAGOS_LGA_COORDS).map(([name, coords]) => (
          <CircleMarker key={`lga-${name}`} center={[coords.lat, coords.lng]}
            radius={3} fillColor="#1e293b" color="#334155" weight={1} fillOpacity={0.6}>
            <Tooltip permanent direction="top" offset={[0, -5]}
              className="lcfpi-lga-label"
              opacity={0.8}>
              <span style={{ fontSize: 8, color: "#64748b", fontFamily: "Arial, sans-serif" }}>{name}</span>
            </Tooltip>
          </CircleMarker>
        ))}

        {/* Project markers */}
        {markers.map((m, i) => (
          <CircleMarker key={m.project_id || i}
            center={[m.lat, m.lng]}
            radius={m.radius}
            fillColor={m.color}
            color="rgba(255,255,255,0.7)"
            weight={2}
            fillOpacity={0.85}
            eventHandlers={{
              click: () => onProjectClick && onProjectClick(m),
            }}
            data-testid={`map-pin-${m.project_id}`}>
            <Popup>
              <div style={{ minWidth: 200, fontFamily: "Arial, sans-serif" }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#0f172a", marginBottom: 4 }}>{m.org_name}</div>
                <div style={{ fontSize: 10, color: "#475569", marginBottom: 6 }}>
                  {m.sector?.replace(/_/g," ")} · {m.lga}, Lagos
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                  {[
                    ["Funding", fmtU(m.funding_need_usd)],
                    ["Readiness", `${(m.readiness_score||0).toFixed(0)}/100`],
                    ["Tier", m.readiness_tier || "—"],
                    ["Eco-Label", m.eco_label || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: "#f8fafc", borderRadius: 4, padding: "4px 6px" }}>
                      <div style={{ fontSize: 8, color: "#94a3b8" }}>{k}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#334155" }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 6, padding: "4px 8px", background: m.color + "18",
                  borderRadius: 4, fontSize: 9, color: m.color, fontWeight: 600 }}>
                  {m.readiness_tier} · {m.status?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}

      </SafeLeafletMap>

      {/* Legend */}
      {showLegend && (
        <div style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(15,23,42,0.92)",
          border: "1px solid #1e293b", borderRadius: 8, padding: "10px 12px", zIndex: 1000 }}>
          <p style={{ fontSize: 8, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em",
            marginBottom: 6, fontWeight: 700 }}>LCFPI Pipeline</p>
          {[["Tier 1 — Finance Ready", "#22c55e"], ["Tier 2 — Needs TA", "#f59e0b"], ["Tier 3 — Early Stage", "#ef4444"]].map(([l, c]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: c, border: "1px solid rgba(255,255,255,.3)" }} />
              <span style={{ fontSize: 9, color: "#94a3b8" }}>{l}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid #1e293b", marginTop: 5, paddingTop: 5, fontSize: 8, color: "#334155" }}>
            {markers.length} projects · Lagos State
          </div>
        </div>
      )}
    </div>
  );
}
